import { useMemo, useState } from 'react';
import { C } from '@ridgewill/brand';
import { adminApi, type AdminUser, type RoleLabels } from '../lib/api.js';
import { useAsync, useAction } from '../lib/useAsync.js';
import { useChips } from '../lib/useChips.js';
import { breakGlassRole, roleLabel, splitRoles } from '../lib/roles.js';
import { count } from '../lib/format.js';
import {
  ChipBar,
  FilterPills,
  Menu,
  MenuItem,
  PrimaryButton,
  Screen,
  inputStyle,
} from '../components/Screen.js';
import { useCan, whyDisabled } from '../lib/capabilities.js';
import {
  AsyncBoundary,
  Badge,
  Button,
  EmptyState,
  Field,
  HoldButton,
  OutcomeNote,
  Panel,
  Table,
  Td,
  When,
} from '../components/ui.js';
import { Modal, ModalActions } from '../components/Modal.js';
import { UserDetail, Status } from './UserDetail.js';

export const PAGE = 50;

/**
 * Accounts, and the things staff actually do to them.
 *
 * ── The row convention, decided once for all five lists ──────────────
 *
 * #63 asked for this to be settled rather than differing per screen, because
 * the old portal's own screens already disagreed — Overview opened on a double
 * click, Agreements on a single one.
 *
 * The decision: A CHECKBOX SELECTS, THE NAME OPENS. Not single-click-selects
 * plus double-click-opens, which is what the old portal had. That pairing is
 * undiscoverable without a tooltip and it fights text selection — `userSelect:
 * none` was set across the table to paper over it, which means an admin cannot
 * copy an email address out of a row.
 *
 * So the email is a LINK and looks like one, the checkbox column does selection,
 * and nothing depends on knowing a gesture. It was a button while the profile
 * was a dialog, which was honest then; the account is an address now, and an
 * address rendered as a button throws away middle click, open-in-new-tab and
 * copy link — the three things somebody keeps a list open to use.
 *
 * ── Search is server-side, and that is not an optimisation ───────────
 *
 * Filtering rows already fetched works until the table is larger than the page
 * limit, at which point searching for somebody who exists returns nothing, with
 * no error and nothing saying the search only covered the first page.
 */
/**
 * People. Agents have their own screen (screens/Agents.tsx, Jon 2026-09-22),
 * so this list asks the server to leave them out.
 */
export function Users({ id, tab }: { id?: string; tab?: string }) {
  /* Keyed, so moving between two accounts remounts rather than reusing the
     previous one's editor state — otherwise a half-typed ban reason follows you
     onto the next person. */
  return id ? <UserDetail key={id} id={id} tab={tab} /> : <UserList />;
}

/** The status pills. `banned` and `unverified` are on every row already. */
const STATUSES = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'banned', label: 'Banned' },
  { value: 'unverified', label: 'Unverified' },
] as const;

function UserList() {
  const [status, setStatus] = useState<StatusFilter>('all');
  const [offset, setOffset] = useState(0);
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [adding, setAdding] = useState(false);
  const mayAdd = useCan('user:create');

  // Any chip change goes back to page one and drops the selection — a tick box
  // on a row that is no longer in the results is an action aimed at nothing.
  const search = useChips([], () => {
    setOffset(0);
    setChecked(new Set());
  });

  /**
   * The role vocabulary, fetched once for the table.
   *
   * Its own request rather than a field on each user row: it is the same answer
   * for every row and it changes when the auth service is released, not when an
   * account is edited. A failure here is silent by design — `roleLabel` falls
   * back to the slug and nothing is highlighted, which is the screen this
   * rendered before labels existed.
   */
  const rolesMeta = useAsync(() => adminApi.roles());
  const known =
    rolesMeta.state.status === 'ready'
      ? {
          labels: rolesMeta.state.data.labels,
          breakGlass: breakGlassRole(rolesMeta.state.data),
        }
      : {};

  const { state, reload } = useAsync(
    () => adminApi.users({ terms: search.active, limit: PAGE, offset, agents: 'exclude' }),
    // `search.key` rather than `search.active`: the array is a new object every
    // render, and depending on it refetches forever.
    [search.key, offset],
  );

  const changePage = (next: number) => {
    setOffset(next);
    setChecked(new Set());
  };

  return (
    <Screen
      filters={
        checked.size > 0 ? (
          <BulkBar
            ids={[...checked]}
            onClear={() => setChecked(new Set())}
            onDone={() => {
              setChecked(new Set());
              reload();
            }}
          />
        ) : (
          <FilterPills
            label="Filter by status"
            current={status}
            onSelect={setStatus}
            options={[...STATUSES]}
          />
        )
      }
      actions={
        <PrimaryButton
          disabled={!mayAdd}
          title={mayAdd ? undefined : whyDisabled('creating accounts')}
          onClick={() => setAdding(true)}
        >
          + Add user
        </PrimaryButton>
      }
      chips={
        <ChipBar
          search={search}
          placeholder="Search name, email or id, then press Enter…"
          appliedByServer={state.status === 'ready' ? state.data.terms : undefined}
        />
      }
    >
      <Panel title="Accounts">
        <AsyncBoundary state={state}>
          {(d) => {
            /**
             * The status pills filter the PAGE, and say so below.
             *
             * Unlike the chips, this is a client-side filter — and it is the one
             * place that is honest, because `banned` and `emailVerified` are on
             * every row already and the server's user listing has no such
             * parameter. The count line names what it is filtering so a narrowed
             * page is never mistaken for a narrowed result set.
             */
            const rows = d.users.filter((u) => matchesStatus(u, status));
            const allChecked = rows.length > 0 && rows.every((u) => checked.has(u.id));

            return (
              <>
                {rows.length === 0 ? (
                  <EmptyState
                    title={
                      search.active.length > 0
                        ? 'No accounts match your search'
                        : status !== 'all'
                          ? `No ${status} accounts on this page`
                          : 'No accounts'
                    }
                    suggestion={
                      search.active.length > 0
                        ? 'Try a different name, email or id, or disable a chip to widen the search.'
                        : status !== 'all'
                          ? 'Other pages may hold some; this pill filters the rows shown.'
                          : undefined
                    }
                  />
                ) : (
                  <Table
                    columns={[
                      <input
                        key="all"
                        type="checkbox"
                        checked={allChecked}
                        aria-label="Select every account on this page"
                        onChange={() =>
                          setChecked(
                            allChecked ? new Set() : new Set(rows.map((u) => u.id)),
                          )
                        }
                      />,
                      'Account',
                      'Roles',
                      'Status',
                      'Created',
                      '',
                    ]}
                  >
                    {rows.map((u) => {
                      const isChecked = checked.has(u.id);
                      return (
                        <tr
                          key={u.id}
                          className="rw-rowhover"
                          style={{
                            background: isChecked ? C.blueTint : undefined,
                            // A banned or closed account is still legible, just
                            // clearly not live. Hiding it would make the list
                            // disagree with the totals.
                            opacity: u.banned || u.pseudonymized ? 0.55 : 1,
                          }}
                        >
                          <Td>
                            <input
                              type="checkbox"
                              checked={isChecked}
                              aria-label={`Select ${u.email ?? u.id}`}
                              onChange={() =>
                                setChecked((cur) => {
                                  const next = new Set(cur);
                                  if (next.has(u.id)) next.delete(u.id);
                                  else next.add(u.id);
                                  return next;
                                })
                              }
                            />
                          </Td>
                          <Td>
                            {/* An ANCHOR since the account became a screen. The
                                row label used to open a dialog, so a button was
                                honest; it is an address now, and rendering an
                                address as a button throws away middle click,
                                open-in-new-tab and copy link — the three things
                                somebody investigating an incident reaches for
                                while keeping the list they are working through. */}
                            <a
                              href={`#/users/${encodeURIComponent(u.id)}`}
                              style={rowLink}
                            >
                              {u.email ?? 'closed account'}
                            </a>
                            {u.name ? <div style={sub}>{u.name}</div> : null}
                            {/* This list excludes agents, but a search by id
                                does not — the id lookup deliberately finds
                                either — so an agent can appear here, and one
                                that looks like a person is the thing this flag
                                exists to prevent. */}
                            {u.isAgent ? (
                              <div style={{ marginTop: '.25rem' }}>
                                <Badge tone="warn">agent</Badge>
                              </div>
                            ) : null}
                          </Td>
                          <Td>
                            <RoleBadges role={u.role} labels={known.labels} breakGlass={known.breakGlass} />
                          </Td>
                          <Td>
                            <Status user={u} />
                          </Td>
                          <Td>
                            <When iso={u.createdAt} />
                          </Td>
                          <Td>
                            <Menu label={`Actions for ${u.email ?? u.id}`}>
                              {(close) => (
                                <>
                                  <MenuItem
                                    href={`#/users/${encodeURIComponent(u.id)}`}
                                    onClick={close}
                                  >
                                    View profile
                                  </MenuItem>
                                  <MenuItem
                                    href={`#/activity?user=${encodeURIComponent(u.id)}`}
                                    onClick={close}
                                  >
                                    View activity
                                  </MenuItem>
                                </>
                              )}
                            </Menu>
                          </Td>
                        </tr>
                      );
                    })}
                  </Table>
                )}

                <Pager
                  shown={rows.length}
                  page={d.users.length}
                  total={d.total}
                  offset={offset}
                  onPage={changePage}
                  filtered={status !== 'all'}
                />

                {/*
                  THE "not in the current results" NOTE IS GONE, and its absence
                  is the point of the refactor.

                  `#/users/abc123` used to find the account among the rows this
                  screen happened to have fetched, so a link opened the profile
                  or rendered an apology depending on the reader's search box.
                  The detail screen fetches the account by id, so the address is
                  the record — see screens/UserDetail.tsx.
                */}
              </>
            );
          }}
        </AsyncBoundary>
      </Panel>

      {adding ? (
        <AddUser
          onClose={() => setAdding(false)}
          reload={reload}
          onCreated={() => {
            setAdding(false);
            reload();
          }}
        />
      ) : null}

    </Screen>
  );
}

type StatusFilter = 'all' | 'active' | 'banned' | 'unverified';

function matchesStatus(u: AdminUser, filter: StatusFilter): boolean {
  if (filter === 'all') return true;
  if (filter === 'banned') return Boolean(u.banned);
  if (filter === 'unverified') return !u.emailVerified;
  return !u.banned && !u.pseudonymized;
}

/**
 * The bulk bar, which takes the FILTERS' place rather than sitting beside them.
 *
 * One slot, two jobs: when nothing is selected the left of the action row is
 * filters, and when something is it is what you can do to the selection. A bar
 * that appears in addition would reflow the table every time a checkbox is
 * ticked.
 */
function BulkBar({
  ids,
  onClear,
  onDone,
}: {
  ids: string[];
  onClear: () => void;
  onDone: () => void;
}) {
  const action = useAction();
  const [confirming, setConfirming] = useState<'ban' | 'unban' | null>(null);

  return (
    <>
      <span style={{ fontSize: '.85rem', fontWeight: 600 }}>
        {count(ids.length)} account{ids.length === 1 ? '' : 's'} selected
      </span>
      <button type="button" onClick={onClear} style={clearLink}>
        Clear
      </button>
      <Button onClick={() => setConfirming('unban')} disabled={action.running}>
        Reinstate
      </Button>
      <Button tone="danger" onClick={() => setConfirming('ban')} disabled={action.running}>
        Ban
      </Button>
      <OutcomeNote outcome={action.outcome} />

      {confirming ? (
        <BulkConfirm
          kind={confirming}
          ids={ids}
          running={action.running}
          onCancel={() => setConfirming(null)}
          onConfirm={(reason) =>
            void action
              .run(async () => {
                /**
                 * Sequential, not `Promise.all`.
                 *
                 * Each of these revokes sessions and writes an activity row, and
                 * firing fifty at once at the admin database is how a bulk
                 * action becomes an incident. It also means a failure halfway
                 * through has a definite meaning: the ones before it happened.
                 */
                let done = 0;
                for (const id of ids) {
                  if (kindIsBan(confirming)) await adminApi.banUser(id, reason);
                  else await adminApi.unbanUser(id);
                  done++;
                }
                return `${kindIsBan(confirming) ? 'Banned' : 'Reinstated'} ${done} of ${ids.length}.`;
              })
              .then(() => {
                setConfirming(null);
                onDone();
              })
          }
        />
      ) : null}
    </>
  );
}

const kindIsBan = (k: 'ban' | 'unban') => k === 'ban';

/** Count-aware, because "Ban 47 accounts" and "Ban this account" are different
 *  decisions and a confirmation that does not say which is not one. */
function BulkConfirm({
  kind,
  ids,
  running,
  onCancel,
  onConfirm,
}: {
  kind: 'ban' | 'unban';
  ids: string[];
  running: boolean;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
}) {
  const [reason, setReason] = useState('');
  const banning = kindIsBan(kind);

  return (
    <Modal
      title={`${banning ? 'Ban' : 'Reinstate'} ${count(ids.length)} account${ids.length === 1 ? '' : 's'}`}
      onClose={onCancel}
      width={460}
      footer={
        <ModalActions>
          <Button onClick={onCancel}>Cancel</Button>
          {banning ? (
            <HoldButton
              disabled={running || !reason.trim()}
              title={`Hold for a second and a half to ban ${count(ids.length)} account${ids.length === 1 ? '' : 's'}.`}
              onHold={() => onConfirm(reason.trim())}
            >
              {running ? 'Working…' : 'Ban them'}
            </HoldButton>
          ) : (
            <Button disabled={running} onClick={() => onConfirm(reason.trim())}>
              {running ? 'Working…' : 'Reinstate them'}
            </Button>
          )}
        </ModalActions>
      }
    >
      {banning ? (
        <>
          <p style={{ margin: 0, fontSize: '.86rem', color: C.textMid, lineHeight: 1.5 }}>
            Their sessions are revoked by the server and they cannot sign in. The same reason is
            recorded on every account.
          </p>
          <Field label="Reason">
            <input value={reason} onChange={(e) => setReason(e.target.value)} style={inputStyle} />
          </Field>
        </>
      ) : (
        <p style={{ margin: 0, fontSize: '.86rem', color: C.textMid, lineHeight: 1.5 }}>
          Restores sign-in for {count(ids.length)} account{ids.length === 1 ? '' : 's'}. Their old
          sessions are not restored. They sign in again.
        </p>
      )}
    </Modal>
  );
}

/**
 * Create an account for somebody and email them their way in (org-admin#83).
 *
 * ── THE ADMIN NO LONGER CHOOSES, OR LEARNS, THE PASSWORD ─────────────
 *
 * This form used to have a third field. The hint under it was honest about what
 * that meant — "You will know this password" — and defended it on the grounds
 * that an admin-created account has to start somewhere and an invite flow is a
 * different feature. That feature now exists: the service generates a password,
 * emails it, and this screen never sees it.
 *
 * What made the old arrangement worth replacing is not that the admin knowing
 * it was a secret leak; it is that the person the account belongs to had a
 * password chosen for them by a colleague and no prompt to replace it. The
 * account is now marked `mustSetPassword`, so the first sign-in asks.
 */
function AddUser({
  onClose,
  onCreated,
  reload,
}: {
  onClose: () => void;
  onCreated: () => void;
  /** Refresh the list WITHOUT closing, for the case where the account was made
   *  and the invitation was not sent — the row must appear even though this
   *  modal stays open to say so. */
  reload: () => void;
}) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const action = useAction();

  return (
    <Modal
      title="Invite a user"
      onClose={onClose}
      width={480}
      footer={
        <ModalActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            disabled={action.running || !email.trim() || !name.trim()}
            onClick={() => {
              /**
               * CLOSES ONLY ON A REAL SUCCESS, and that is a fix rather than a
               * refinement.
               *
               * This used to be `action.run(...).then(onCreated)`. `run` catches
               * whatever the body throws and records it as a failed outcome, then
               * RESOLVES — so `.then` fired on every path, the modal closed, and
               * the `OutcomeNote` that was about to explain the failure was
               * unmounted before anybody read it. A duplicate address has been
               * silently dismissing itself that way.
               *
               * It matters more now: "the account exists but we could not email
               * them" is an outcome the admin has to act on, and it is the one
               * the old shape was guaranteed to throw away.
               */
              let created = false;
              void action
                .run(async () => {
                  const { delivered } = await adminApi.inviteUser({
                    email: email.trim(),
                    name: name.trim(),
                  });
                  if (!delivered) {
                    /* The row exists, so the list must show it even though this
                       modal stays open to explain what did not happen. */
                    reload();
                    throw new Error(
                      'Account created, but the invitation could not be sent. Send a password reset from their profile.',
                    );
                  }
                  created = true;
                  return 'Account created and the invitation is on its way.';
                })
                .then(() => {
                  if (created) onCreated();
                });
            }}
          >
            {action.running ? 'Inviting…' : 'Send invitation'}
          </Button>
        </ModalActions>
      }
    >
      <OutcomeNote outcome={action.outcome} />
      <Field label="Email">
        <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" style={inputStyle} />
      </Field>
      <Field label="Name">
        <input value={name} onChange={(e) => setName(e.target.value)} style={inputStyle} />
      </Field>
      <p style={{ margin: 0, fontSize: '.8rem', color: C.muted, lineHeight: 1.5 }}>
        They will be emailed a temporary password and asked to choose their own the first time
        they sign in. The account starts with the default role and no application access. Set
        roles from its profile.
      </p>
    </Modal>
  );
}

export function Pager({
  shown,
  page,
  total,
  offset,
  onPage,
  filtered,
}: {
  shown: number;
  page: number;
  total: number;
  offset: number;
  onPage: (offset: number) => void;
  filtered: boolean;
}) {
  const from = total === 0 ? 0 : offset + 1;
  const to = offset + page;
  return (
    <div style={pager}>
      <span style={{ fontSize: '.82rem', color: C.muted }}>
        {count(from)}–{count(to)} of {count(total)}
        {filtered && shown !== page ? ` · ${count(shown)} shown after filtering this page` : ''}
      </span>
      <span style={{ display: 'flex', gap: '.4rem', marginLeft: 'auto' }}>
        <Button disabled={offset === 0} onClick={() => onPage(Math.max(0, offset - PAGE))}>
          Previous
        </Button>
        <Button disabled={to >= total} onClick={() => onPage(offset + PAGE)}>
          Next
        </Button>
      </span>
    </div>
  );
}

/**
 * The roles on an account, by their display names.
 *
 * ── THE HIGHLIGHTED ROLE WAS ONE THAT NO LONGER EXISTS ───────────────
 *
 * This tinted `admin` amber. RidgeWill/auth#270 replaced two roles with five and
 * `admin` became `auth-admin`, so the condition stopped matching anything and
 * every role has rendered the same neutral grey since — a highlight that was
 * removed by a rename in another repository, with nothing failing.
 *
 * It now marks the BREAK-GLASS role, which is the one worth picking out of a
 * table of forty rows: it is the only role that can act on other staff, the only
 * one a group may not carry, and the answer to "who can do anything here".
 * Named from a constant rather than a literal so the next rename moves it.
 */
export function RoleBadges({
  role,
  labels,
  breakGlass,
}: {
  role?: string | null;
  labels?: RoleLabels;
  breakGlass?: string;
}) {
  const roles = useMemo(() => splitRoles(role), [role]);
  if (roles.length === 0) return <span style={{ color: C.muted }}>—</span>;
  return (
    <span style={{ display: 'inline-flex', gap: '.3rem', flexWrap: 'wrap' }}>
      {roles.map((r) => (
        <Badge key={r} tone={r === breakGlass ? 'warn' : 'neutral'}>
          {roleLabel(r, labels)}
        </Badge>
      ))}
    </span>
  );
}

const rowLink = {
  fontWeight: 600,
  color: C.brand,
  textDecoration: 'none',
};

const sub = { fontSize: '.78rem', color: C.muted, marginTop: 2 };

const clearLink = {
  background: 'none',
  border: 'none',
  color: C.brand,
  font: 'inherit',
  fontSize: '.82rem',
  cursor: 'pointer',
  padding: '.2rem .3rem',
};

const pager = {
  display: 'flex',
  alignItems: 'center',
  gap: '.75rem',
  padding: '.7rem 1.2rem',
  borderTop: `1px solid ${C.border}`,
};
